/**
 * מסך "פרופיל"  —  /profile
 * פרטי המשתמש ושינוי סיסמה.
 */
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import { getApiError } from '../utils/apiError';

const ProfilePage: React.FC = () => {
  const { user, refreshUser } = useAuth();


  // 2FA enrolment: setup2FA returns a QR image the user scans, then they confirm
  // with a code from their authenticator app.
  const [qrCode, setQrCode] = useState('');
  const [totpToken, setTotpToken] = useState('');
  const [twoFAError, setTwoFAError] = useState('');
  const [busy2FA, setBusy2FA] = useState(false);

  const startEnable2FA = async () => {
    setTwoFAError('');
    setBusy2FA(true);
    try {
      const setup = await authService.setup2FA();
      setQrCode(setup.qr_code);
    } catch (err: any) {
      setTwoFAError(getApiError(err, 'לא הצלחנו להתחיל את הגדרת האימות הדו-שלבי'));
    } finally {
      setBusy2FA(false);
    }
  };

  const confirmEnable2FA = async () => {
    setTwoFAError('');
    setBusy2FA(true);
    try {
      await authService.enable2FA(totpToken);
      setQrCode('');
      setTotpToken('');
      await refreshUser();
      toast.success('האימות הדו-שלבי הופעל');
    } catch (err: any) {
      setTwoFAError(getApiError(err, 'הקוד שהוזן אינו תקף'));
    } finally {
      setBusy2FA(false);
    }
  };

  const disable2FA = async () => {
    setTwoFAError('');
    setBusy2FA(true);
    try {
      await authService.disable2FA(totpToken);
      setTotpToken('');
      await refreshUser();
      toast.success('האימות הדו-שלבי כובה');
    } catch (err: any) {
      setTwoFAError(getApiError(err, 'הקוד שהוזן אינו תקף'));
    } finally {
      setBusy2FA(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.dark', mb: 3 }}>
        פרופיל
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            פרטי המשתמש
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2">
              <strong>שם:</strong> {user?.full_name || `${user?.first_name} ${user?.last_name}`}
            </Typography>
            <Typography variant="body2">
              <strong>אימייל:</strong> {user?.email}
            </Typography>
            {user?.sub_clinic && (
              <Typography variant="body2">
                <strong>מחוז:</strong> {user.sub_clinic}
              </Typography>
            )}
            <Typography variant="body2">
              <strong>אזור זמן:</strong> {user?.timezone}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* The change-password form used to live here, posting to
          /auth/change-password - an endpoint the server does not have. It
          failed every time, with a generic "we could not update the password",
          and left the coordinator believing the system was broken rather than
          that the feature was missing. The reset-by-email route exists and
          works (verified: the token is written with an hour's expiry), so she
          is sent there instead. */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            סיסמה
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            להחלפת הסיסמה נשלח אליך קישור בדוא"ל.
          </Typography>
          <Button
            variant="outlined"
            component={RouterLink}
            to="/forgot-password"
          >
            שליחת קישור להחלפת סיסמה
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            אימות דו-שלבי
          </Typography>
          <Stack spacing={2}>
            {twoFAError && <Alert severity="error">{twoFAError}</Alert>}

            {user?.is_2fa_enabled ? (
              <>
                <Alert severity="success">האימות הדו-שלבי פעיל בחשבון הזה.</Alert>
                <Divider />
                <TextField
                  label="קוד אימות"
                  value={totpToken}
                  onChange={(e) => setTotpToken(e.target.value)}
                  inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                  helperText="כדי לכבות, יש להזין קוד תקף מהאפליקציה"
                  fullWidth
                />
                <Box>
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={disable2FA}
                    disabled={busy2FA || totpToken.length < 6}
                  >
                    כיבוי אימות דו-שלבי
                  </Button>
                </Box>
              </>
            ) : qrCode ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  יש לסרוק את הקוד באפליקציית האימות, ואז להזין את הקוד בן 6 הספרות שהיא מציגה.
                </Typography>
                <Box
                  component="img"
                  src={qrCode}
                  alt="קוד QR לאימות דו-שלבי"
                  sx={{ width: 180, height: 180, alignSelf: 'flex-start' }}
                />
                <TextField
                  label="קוד אימות"
                  value={totpToken}
                  onChange={(e) => setTotpToken(e.target.value)}
                  inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                  fullWidth
                />
                <Box>
                  <Button
                    variant="contained"
                    onClick={confirmEnable2FA}
                    disabled={busy2FA || totpToken.length < 6}
                  >
                    הפעלת אימות דו-שלבי
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  הוספת שלב אימות שני לכניסה, באמצעות אפליקציית אימות.
                </Typography>
                <Box>
                  <Button variant="contained" onClick={startEnable2FA} disabled={busy2FA}>
                    הגדרת אימות דו-שלבי
                  </Button>
                </Box>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProfilePage;
